import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { Priority } from '../lib/types';
import { Modal } from '../components/Modal';
import { DateCell } from '../components/DateCell';

interface Props {
  companyId: string;
  /** Whether this person may set a budget — the rate card's own grant. */
  mayBudget?: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateProjectModal({ companyId, mayBudget = false, onClose, onCreated }: Props) {
  const [budget, setBudget] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>('medium');
  const [startDate, setStartDate] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [gitlabProjectPath, setGitlabProjectPath] = useState('');
  const [gitlabDefaultBranch, setGitlabDefaultBranch] = useState('');
  const [billable, setBillable] = useState(true);
  const [restricted, setRestricted] = useState(false);
  // Off by default: nobody should be exposed to a customer by a forgotten checkbox.
  const [sharedWithClient, setSharedWithClient] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function onNameChange(value: string) {
    const previous = suggestCode(name);
    setName(value);
    if (!code || code === previous) setCode(suggestCode(value));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/projects`, {
        name,
        code: code.toUpperCase(),
        description: description || undefined,
        priority,
        startDate: startDate || undefined,
        targetDate: targetDate || undefined,
        gitlabProjectPath: gitlabProjectPath || undefined,
        gitlabDefaultBranch: gitlabDefaultBranch || undefined,
        billable,
        restricted,
        sharedWithClient,
        ...(mayBudget && budget.trim() !== '' ? { budget: Number(budget.replace(/[\s,R]/g, '')) } : {}),
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the project');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Add project"
      subtitle="The project gets the standard board straight away; columns can be changed per project later."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="create-project" className="btn btn-primary" disabled={busy}>
            {busy ? 'Adding…' : 'Add project'}
          </button>
        </>
      }
    >
      {error && <div className="callout">{error}</div>}
      <form id="create-project" onSubmit={submit} noValidate>
        <label className="field">
          <span className="field-label">Project name</span>
          <input type="text" value={name} onChange={(e) => onNameChange(e.target.value)} required autoFocus />
        </label>

        <label className="field">
          <span className="field-label">Short code</span>
          <input type="text" className="mono" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={12} required />
          <span className="field-hint">Used in work-item keys. Unique within this company.</span>
        </label>

        <label className="field">
          <span className="field-label">Description (optional)</span>
          <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>

        <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
          <label className="field grow">
            <span className="field-label">Priority</span>
            <select value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
              <option value="none">None</option>
            </select>
          </label>

          <div className="field grow">
            <span className="field-label">Start date</span>
            <DateCell
              value={startDate || null}
              editable
              emptyLabel="Pick a date"
              max={targetDate || undefined}
              onChange={(next) => setStartDate(next ?? '')}
            />
          </div>

          <div className="field grow">
            <span className="field-label">Target date</span>
            <DateCell
              value={targetDate || null}
              editable
              emptyLabel="Pick a date"
              min={startDate || undefined}
              onChange={(next) => setTargetDate(next ?? '')}
            />
          </div>
        </div>

        {mayBudget && (
          <label className="field">
            <span className="field-label">Budget in rand (optional)</span>
            <input type="text" inputMode="decimal" value={budget} placeholder="e.g. 40000" onChange={(event) => setBudget(event.target.value)} />
          </label>
        )}

        <label className="field">
          <span className="field-label">GitLab project path (optional)</span>
          <input
            type="text"
            className="mono"
            value={gitlabProjectPath}
            onChange={(e) => setGitlabProjectPath(e.target.value)}
            placeholder="lucentra-systems/teamwork"
          />
          <span className="field-hint">Links the repo so merges can be approved and actioned from the Hub.</span>
        </label>

        <label className="field">
          <span className="field-label">Default branch (optional)</span>
          <input type="text" className="mono" value={gitlabDefaultBranch} onChange={(e) => setGitlabDefaultBranch(e.target.value)} placeholder="develop" />
        </label>

        <div className="checkbox-row">
          <input id="billable" type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} />
          <label htmlFor="billable">
            <strong>Billable</strong>
            <div className="dim" style={{ fontSize: 12 }}>
              Hours booked here are chargeable by default, and count towards what the customer
              is invoiced. {/*
                SAID HERE BECAUSE THIS IS THE ONLY CHANCE TO GET IT RIGHT. It cannot be changed
                afterwards — every hour booked inherits it, so flipping it later would leave a
                project whose own entries disagree with it. Somebody ticking past this box
                deserves to know that before they tick it, not when they try to undo it.
              */}
              <strong> Permanent once the project exists.</strong>
            </div>
          </label>
        </div>

        <div className="checkbox-row">
          <input
            id="shared"
            type="checkbox"
            checked={sharedWithClient}
            onChange={(e) => setSharedWithClient(e.target.checked)}
          />
          <label htmlFor="shared">
            <strong>Share with the client</strong>
            <div className="dim" style={{ fontSize: 12 }}>
              The customer's own people can see and work on this project. Leave it off for our
              internal work on their account.
            </div>
          </label>
        </div>

        <div className="checkbox-row">
          <input id="restricted" type="checkbox" checked={restricted} onChange={(e) => setRestricted(e.target.checked)} />
          <label htmlFor="restricted">
            <strong>Restricted to the project team</strong>
            <div className="dim" style={{ fontSize: 12 }}>
              Even our own staff need to be on the team to open it. You are added automatically.
            </div>
          </label>
        </div>
      </form>
    </Modal>
  );
}

function suggestCode(name: string): string {
  const words = name.trim().toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 6);
  return words.map((w) => w[0]).join('').slice(0, 6);
}
